import Button from "@core/ui/Button";
import Loader from "@core/ui/Loader";
import { yupResolver } from "@hookform/resolvers/yup";
import cn from "classnames";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import OtpInput from "react-otp-input";
import * as yup from "yup";

import {
  sendOneTimeCodeToExistingUser,
  testOneTimeCodeForExistingUser,
} from "../services/nocd-api";

interface OneTimeCodeFormProps {
  onSuccess: (oneTimeCode: string) => void;
  email: string;
  title: string;
  description: string;
  className?: string;
}

const schema = yup.object({
  oneTimeCode: yup.string().required("Enter your verification code"),
});
type FormValues = yup.TypeOf<typeof schema>;

export default function OneTimeCodeForm({
  onSuccess,
  email,
  title,
  description,
  className,
}: OneTimeCodeFormProps): JSX.Element {
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isResendingCode, setIsResendingCode] = useState(false);

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const { control, formState } = form;
  const { isSubmitting, errors } = formState;

  const onSubmit = form.handleSubmit(async ({ oneTimeCode }: FormValues) => {
    return testOneTimeCodeForExistingUser(email, oneTimeCode)
      .then(({ status }) => {
        if (status === "fail") {
          throw new Error(`Invalid one time code`);
        }
      })
      .then(() => onSuccess(oneTimeCode))
      .catch(() =>
        form.setError("oneTimeCode", {
          message: "Invalid one time code.",
        })
      );
  });

  useEffect(() => {
    setIsSendingCode(true);

    sendOneTimeCodeToExistingUser(email)
      .catch((error: Error) =>
        toast.error(error?.message ?? "An unknown error occurred")
      )
      .finally(() => setIsSendingCode(false));
  }, [email]);

  if (isSendingCode) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader className="text-32px text-teal-600" />
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={cn("mx-auto font-poppins", className)}>
      <h2 className="text-center text-22px font-bold tablet:text-24px">
        {title}
      </h2>

      <p className="mt-2 text-center text-16px text-gray-600">{description}</p>

      <div className="mt-10">
        <Controller
          control={control}
          name="oneTimeCode"
          render={({ field: { onChange, value } }) => (
            <OtpInput
              value={value}
              onChange={onChange}
              numInputs={5}
              renderInput={(props) => <input {...props} />}
              containerStyle={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                gap: "8px",
                margin: "40px 0 16pxDidn",
              }}
              inputStyle={{
                width: "100%",
                height: "52px",
                borderRadius: "8px",
                border: "2px solid #E5E7EB",
                backgroundColor: "#FFFFFF",
                textAlign: "center",
              }}
            />
          )}
        />
        {errors.oneTimeCode && (
          <p className="mt-1 text-center text-14px text-red-600">
            {errors.oneTimeCode.message}
          </p>
        )}
      </div>

      <div className="flex flex-col items-center mt-10">
        <Button
          disabled={isSubmitting}
          type="submit"
          loading={isSubmitting}
          className="h-13 font-semibold text-18px px-6 w-full font-poppins"
          color="indigo"
          rounded="lg"
        >
          Next
        </Button>

        <div className="text-center text-gray-700 mt-4">
          Didn&apos;t receive a code?{" "}
          <Button
            color="indigo"
            variant="text"
            disabled={isResendingCode}
            onClick={() => {
              setIsResendingCode(true);

              const promise = sendOneTimeCodeToExistingUser(email).then(() =>
                setIsResendingCode(false)
              );

              // eslint-disable-next-line @typescript-eslint/no-floating-promises
              toast.promise(promise, {
                success: "Code resent",
                loading: "Resending code...",
                error: (error: Error) => error.message,
              });

              return promise;
            }}
          >
            Click here to resend one
          </Button>
        </div>
      </div>
    </form>
  );
}
